import Dexie, { type EntityTable } from 'dexie';

export interface Subject {
  id?: number;
  name: string;
  createdAt: string;
}

export interface MockExam {
  id?: number;
  examType: string;
  date: string;
  title: string;
  score: number;
  nets: Record<string, number>;
  createdAt: string;
}

export interface Teacher {
  id?: number;
  name: string;
  createdAt: string;
  avatarUrl?: string;
}

export interface Playlist {
  id?: number;
  subjectId: number;
  url: string;
  totalDurationMs: number;
  videos: VideoItem[];
  createdAt: string;
}

export interface VideoItem {
  id: string; // YouTube Video ID
  title: string;
  durationMs: number;
  aiTopic?: string; // Categorized by AI
  teacherName?: string;
  volumeInfo?: string;
  lessonName?: string;
  originalTitle?: string;
}

export interface StudyPlan {
  id?: number;
  subjectId: number;
  startDate: string;
  deadlineDate: string;
  dailyTargetMs: number;
  allowedDays: number[]; // e.g. [1,2,3,4,5] for Mon-Fri
  createdAt: string;
  sharedSlug?: string;
  isPublic?: boolean;
  gradeLevel?: string;
  exam?: string;
  isPaused?: boolean;
  pausedAt?: string;
  books?: any[];
}


export interface ScheduleItem {
  id?: number;
  studyPlanId: number;
  videoId: string;
  videoTitle: string;
  durationMs: number;
  assignedDate: string; // YYYY-MM-DD
  isCompleted: boolean;
  aiTopic?: string;
  teacherName?: string;
  notes?: string;
  /** Not ilk kez yazıldığında set edilir (ISO tarih), sonradan değişmez. */
  noteCreatedAt?: string;
  /** Not her kaydedildiğinde güncellenir (ISO tarih). */
  noteUpdatedAt?: string;
  completedAt?: string;
  originalTitle?: string;
  isLocked?: boolean;
  volumeInfo?: string;
  lessonName?: string;
  orderIndex?: number;
  channelName?: string;
  isBookmarked?: boolean;
  linkedTestCode?: string;
}

export interface Flashcard {
  id?: number;
  subjectId: number;         // Hangi derse ait olduğu (db.subjects ilişkisi)
  front: string;             // Kartın ön yüzü (Soru, Terim, Formül vb.)
  back: string;              // Kartın arka yüzü (Cevap, Açıklama, Görsel vb.)
  interval: number;          // Bir sonraki gösterim aralığı (gün bazında, varsayılan: 1)
  easeFactor: number;        // Kolaylık katsayısı (SM-2 Ease Factor, varsayılan: 2.5)
  repetitions: number;       // Üst üste doğru bilinme sayısı (varsayılan: 0)
  nextReviewDate: string;    // Bir sonraki tekrar tarihi (ISO string: YYYY-MM-DD)
  createdAt: string;         // Oluşturulma tarihi
}

export interface Settings {
  id?: number;
  geminiApiKey: string;
  youtubeApiKey?: string;
  targetExam?: string;
  targetExamDate?: string;
  streakCount?: number;
  bestStreakCount?: number;
  lastStudyDate?: string;
  navbarPosition?: 'left' | 'top';
  theme?: 'light' | 'dark';
  accentColor?: string;
  lastBackupDate?: string;
  pomodoroEnabled?: boolean;
  xp?: number;
  level?: number;
  gradeLevel?: string;
  userGoal?: string;
  purpose?: string;
  exam?: string;
  secondaryTargetExam?: string;
  showExamCountdown?: boolean;
}

const db = new Dexie('TurarkYoutubePlannerDB') as Dexie & {
  subjects: EntityTable<Subject, 'id'>;
  playlists: EntityTable<Playlist, 'id'>;
  studyPlans: EntityTable<StudyPlan, 'id'>;
  scheduleItems: EntityTable<ScheduleItem, 'id'>;
  settings: EntityTable<Settings, 'id'>;
  teachers: EntityTable<Teacher, 'id'>;
  mockExams: EntityTable<MockExam, 'id'>;
  flashcards: EntityTable<Flashcard, 'id'>;
};

// Schema declaration
db.version(6).stores({
  subjects: '++id, name',
  playlists: '++id, subjectId',
  studyPlans: '++id, subjectId, deadlineDate',
  scheduleItems: '++id, studyPlanId, assignedDate, isCompleted, videoId, isLocked',
  settings: '++id',
  teachers: '++id, &name',
  mockExams: '++id, examType, date, score'
});

// v7: Bilgi Kartı (Flashcard / Spaced Repetition) desteği eklendi.
// Var olan tablolar burada tekrar listelenmiyor — Dexie önceki versiyonlardaki
// şemayı korur, sadece yeni tablo eklenmiş oluyor.
db.version(7).stores({
  flashcards: '++id, subjectId, nextReviewDate'
});

export const dbState = {
  isRestoring: false
};

// Debounce helper
function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: any;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

const triggerBackup = debounce(() => {
  if (dbState.isRestoring) return;
  window.dispatchEvent(new Event('force-backup'));
}, 2000);

// Add hooks to all relevant tables
const tablesToWatch = ['subjects', 'playlists', 'studyPlans', 'scheduleItems', 'teachers', 'mockExams', 'flashcards'];
tablesToWatch.forEach(tableName => {
  const table = db.table(tableName);
  table.hook('creating', () => { triggerBackup(); });
  table.hook('updating', () => { triggerBackup(); });
  table.hook('deleting', () => { triggerBackup(); });
});

// For settings table, ignore updates that only modify 'lastBackupDate'
db.settings.hook('updating', (mods) => {
  const modKeys = Object.keys(mods);
  if (modKeys.length === 1 && modKeys[0] === 'lastBackupDate') {
    return;
  }
  triggerBackup();
});
db.settings.hook('creating', () => { triggerBackup(); });
db.settings.hook('deleting', () => { triggerBackup(); });

export { db };
